import { Component, OnDestroy, OnInit } from '@angular/core';
import { CreativesetDataService } from '@app/shared/creativeset';
import { VersionsService } from '@app/shared/versions/state/versions.service';
import {
    IUIListDataNode,
    UIDebounce,
    UIDialogComponent,
    UIListDataSource,
    UINotificationService
} from '@bannerflow/ui';
import { concatLatestFrom } from '@ngrx/effects';
import { IDuplicationResponse, ISetsAndFolderItem, ISetsAndFoldersResponse } from '@studio/domain';
import { CreativeSize } from '@studio/domain/creativeset/size';
import { IVersion } from '@studio/domain/creativeset/version';
import { filter, Subject, takeUntil } from 'rxjs';
import { clearWildcards } from '../duplicate-creatives/state/duplicate-creative.utils';
import { DuplicateCreativesService } from '../duplicate-creatives/state/duplicate-creatives.service';
import { TileSelectService } from '../services';
import { ISelectableSize } from '../size-add-dialog/size-list/size-list.component.type';
import { ISelectableVersion } from './version-list/version-list.component';
import { UserService } from '@app/shared/user/state/user.service';
import { ListHubService } from '../duplicate-creatives/list-hub.service';

@Component({
    selector: 'duplicate-creative-dialog',
    templateUrl: './duplicate-creative-dialog.component.html',
    styleUrls: ['./duplicate-creative-dialog.component.scss'],
    host: {
        '[attr.data-test-id]': '"duplicate-creative-dialog"'
    }
})
export class DuplicateCreativeDialogComponent implements OnInit, OnDestroy {
    dataSource: UIListDataSource<IListItem>;

    page = 1;
    totalPages = 1;
    showLoadMore = false;
    disableDuplication = true;
    duplicateToNew: boolean;

    sizeList: ISelectableSize[];
    selectedSizes: CreativeSize[];

    versions: IVersion[] = [];

    versionList: ISelectableVersion[];
    selectedVersions: IVersion[];

    targetCreativesetId?: string;
    targetFolder?: string;

    breadcrumbs: IListItem[] = [];
    currentItems: ISetsAndFolderItem[] = [];

    searchTerm: string;

    isPendingRequest: boolean;

    private unsubscribe$ = new Subject<void>();

    private defaultVersion: IVersion;

    constructor(
        private dialog: UIDialogComponent,
        private creativesetDataService: CreativesetDataService,
        private tileSelectService: TileSelectService,
        private notification: UINotificationService,
        private versionsService: VersionsService,
        public duplicateCreativesService: DuplicateCreativesService,
        private userService: UserService,
        private listHubService: ListHubService
    ) {}

    ngOnInit(): void {
        this.loadCreativeSets();
        this.listHubService.startConnection();

        this.duplicateCreativesService.error$
            .pipe(
                takeUntil(this.unsubscribe$),
                filter(error => !!error),
                concatLatestFrom(() => this.duplicateCreativesService.isDuplicating$)
            )
            .subscribe(([error, isDuplicating]: [any, boolean]) => {
                if (isDuplicating) {
                    this.displayErrorNotification(`Error while duplicating: ${error.statusText}`);
                    return;
                }
                this.displayErrorNotification(`Error while getting items`);
            });

        this.duplicateCreativesService.itemsPage$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(({ searchTerm, setsAndFolders }) => {
                if (!this.breadcrumbs.length) {
                    this.searchTerm = searchTerm ?? '';
                }
                this.setItemsFromResponse(setsAndFolders);
            });

        this.duplicateCreativesService.duplicationResponse$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(response => {
                if (!response) {
                    return;
                }
                this.displayNotification(response);
                this.closeDialog();
            });

        this.duplicateCreativesService.loaded$.pipe(takeUntil(this.unsubscribe$)).subscribe(loaded => {
            this.isPendingRequest = !loaded;
        });

        this.versionsService.defaultVersion$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(defaultVersion => {
                this.defaultVersion = defaultVersion;
            });

        this.duplicateToNew = this.dialog.config.data.duplicateToNew;

        this.versionsService.versions$.pipe(takeUntil(this.unsubscribe$)).subscribe(versions => {
            this.initVersionLists(versions);
        });
    }

    ngOnDestroy(): void {
        this.duplicateCreativesService.clearSearch();
        this.listHubService.destroy();
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    onSelectionChange(selection: IUIListDataNode[]): void {
        const selected = selection[0] as IListItem;
        if (!selected || this.isPendingRequest) {
            return;
        }

        if (selected.type === 'Folder') {
            if (selected.id === this.targetFolder) {
                return;
            }
            this.searchTerm = '';
            this.breadcrumbs.push(selected);
            this.loadCreativeSets(selected.id);
        } else if (selected.type === 'Back') {
            if (this.breadcrumbs.length === 1) {
                this.breadcrumbs = [];
                if (this.searchTerm) {
                    this.searchTermChanged(this.searchTerm);
                } else {
                    this.loadCreativeSets();
                }
            } else {
                const targetFolder = this.breadcrumbs[this.breadcrumbs.length - 2];
                this.breadcrumbs.pop();
                this.loadCreativeSets(targetFolder.id);
            }
        } else {
            this.targetCreativesetId = selected.id;
        }

        this.checkDuplicationDisabled();
    }

    navigateToFolder(id?: string, index?: number): void {
        if (this.targetFolder === id) {
            return;
        }

        if (!id) {
            this.breadcrumbs = [];
            if (this.searchTerm) {
                this.searchTermChanged(this.searchTerm);
            } else {
                this.loadCreativeSets();
            }
            return;
        }
        this.breadcrumbs.splice(index! + 1, this.breadcrumbs.length - index! - 1);
        this.loadCreativeSets(id);
    }

    loadMoreData(): void {
        if (this.searchTerm) {
            this.duplicateCreativesService.search(this.searchTerm, this.page + 1);
            return;
        }
        this.duplicateCreativesService.loadPage(this.targetFolder, this.page + 1);
    }

    closeDialog(): void {
        this.dialog.dialogRef.close();
        this.duplicateCreativesService.clearState();
    }

    duplicateCreatives(): void {
        const selectedSizesIds = this.selectedSizes.map(s => s.id);
        const selectedVersionsIds = this.selectedVersions.map(v => v.id);
        if (this.duplicateToNew) {
            this.duplicateToFolder(selectedSizesIds, selectedVersionsIds);
        } else {
            this.duplicateToCreativeset(selectedSizesIds, selectedVersionsIds);
        }
    }

    sizesChanged(sizes: CreativeSize[]): void {
        this.selectedSizes = sizes;
        this.checkDuplicationDisabled();
    }

    versionsChanged(versions: IVersion[]): void {
        this.selectedVersions = versions;
        this.checkDuplicationDisabled();
    }

    @UIDebounce(300)
    searchTermChanged(newTerm: string): void {
        if (!clearWildcards(newTerm)) {
            this.duplicateCreativesService.clearSearch();
            this.loadCreativeSets();
            return;
        }
        if (!this.searchTerm) {
            this.page = 1;
            this.totalPages = 1;
            this.showLoadMore = false;
        }
        this.duplicateCreativesService.search(newTerm, this.page);
    }

    private initVersionLists(versions: IVersion[]): void {
        const selectedCreatives = this.tileSelectService.getSelected();
        this.versions = versions;
        this.versionList = this.versions.map(version => {
            const selected = !!selectedCreatives.find(c => c.version.id === version.id);
            const versionName =
                version.id === this.defaultVersion?.id ? `${version.name} (default)` : version.name;
            return { ...version, selected, id: version.id, name: versionName };
        });
        this.selectedVersions = this.versionList.filter(v => v.selected);
        this.sizeList = this.creativesetDataService.creativeset.sizes.map(size => {
            const selected = !!selectedCreatives.find(c => c.size.id === size.id);
            return { ...size, selected, id: size.id };
        });
        this.selectedSizes = this.sizeList.filter(s => s.selected);

        this.checkDuplicationDisabled();
    }

    private loadCreativeSets(folderId?: string): void {
        this.page = 1;
        this.totalPages = 1;
        this.targetFolder = folderId;
        this.duplicateCreativesService.loadPage(this.targetFolder, this.page);
    }

    private async duplicateToFolder(
        selectedSizesIds: string[],
        selectedVersionsIds: string[]
    ): Promise<void> {
        const creativesetId = this.creativesetDataService.creativeset.id;
        const isCommonListViewEnabled = await this.userService.hasPermission('CommonListView');

        this.duplicateCreativesService.duplicateToNew(
            creativesetId,
            selectedSizesIds,
            selectedVersionsIds,
            this.targetFolder ?? '',
            isCommonListViewEnabled
        );
    }

    private duplicateToCreativeset(selectedSizesIds: string[], selectedVersionsIds: string[]): void {
        if (!this.targetCreativesetId) {
            throw new Error('Missing target for duplication');
        }

        const creativesetId = this.creativesetDataService.creativeset.id;

        this.duplicateCreativesService.duplicateToExisting(
            creativesetId,
            selectedSizesIds,
            selectedVersionsIds,
            this.targetCreativesetId
        );
    }

    private checkDuplicationDisabled(): void {
        if (this.duplicateToNew) {
            this.disableDuplication = !this.selectedSizes.length || !this.selectedVersions.length;
        } else {
            this.disableDuplication = !this.selectedSizes.length || !this.targetCreativesetId;
        }
    }

    private displayNotification(response: IDuplicationResponse): void {
        const url = `brand/${response.brandId}/creativeset/${response.creativeSetId}`;
        this.notification.open(
            `Creatives are being duplicated.<br>
    <a href="${url}" target="_blank" rel="noopener noreferrer">Go to target creative set</a>`,
            { type: 'success', placement: 'top', autoCloseDelay: 5000 }
        );
    }

    private displayErrorNotification(message: string): void {
        this.notification.open(message, { type: 'error', placement: 'top' });
    }

    private setItemsFromResponse(response?: ISetsAndFoldersResponse): void {
        this.totalPages = response?.totalPages ?? 1;
        this.page = response?.pageIndex ?? 1;
        this.showLoadMore = this.page < this.totalPages;

        const items =
            this.page > 1 ? [...this.currentItems, ...(response?.items ?? [])] : response?.items ?? [];

        const newDataSource: IListItem[] = [];

        if (this.targetFolder) {
            newDataSource.push({ name: 'Back', type: 'Back', id: 'Back', disabled: false });
        }

        const currentCreativesetId = this.creativesetDataService.creativeset.id;

        for (const item of items) {
            const disabled = this.duplicateToNew
                ? item.type === 'Creativeset'
                : item.id === currentCreativesetId;

            newDataSource.push({
                ...item,
                disabled
            });
        }

        this.currentItems = items;
        this.dataSource = new UIListDataSource<IListItem>(newDataSource);
    }
}

interface IListItem extends ISetsAndFolderItem {
    disabled: boolean;
}
